import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import useDispatch from 'hooks/useDispatch'
import { selectCurrentMerchant } from 'selectors/merchant'
import { resetForm } from 'slices/orderFeedbackGuestExperience'
import { selectIsFormDirty } from 'slices/orderFeedbackGuestExperience/selectors'

// Same shape as useReplyAddressFormInitialize/useFeedbackCollectionFormInitialize:
// key the effect on the merchant's IDENTITY, not the object reference, so a
// successful PATCH (which invalidates the merchant cache and produces a new
// object reference for the same merchant) does not fire again and clobber an
// in-progress edit.
export const useGuestExperienceFormInitialize = () => {
  const dispatch = useDispatch()
  const merchant = useSelector(selectCurrentMerchant)
  const merchantId = merchant?.id

  // AI-1283: the id-keyed dep above only survives a RE-RENDER.
  // GuestExperiencePane UNMOUNTS when the merchant switches Settings sub-tabs,
  // so on the way back this effect runs again from scratch — and with no dirty
  // guard it would reset the toggle and the acknowledgment message to the
  // merchant's last-persisted values, discarding an edited, unpublished
  // message with no warning. A ref, not a dependency: reading the LATEST dirty
  // flag at effect-run time is enough to guard against that, without adding a
  // dependency that would re-run this same effect on every dirty <-> clean
  // flip — in particular right after Publish, before the merchant record has
  // necessarily caught up. Same guard, for the same reason, as
  // useReplyAddressFormInitialize and useFeedbackCollectionFormInitialize.
  const isFormDirtyRef = useRef(false)
  isFormDirtyRef.current = useSelector(selectIsFormDirty)

  useEffect(() => {
    if (isFormDirtyRef.current) return

    if (merchant) {
      dispatch(
        resetForm({
          // `??`, not `||`: `false` is a legitimate, already-loaded value for
          // callTheStoreEnabled, so `||` would silently coerce it back to
          // `true` while masking a genuinely absent field the same way it
          // masks a real one — `??` only falls back when the field is
          // null/undefined, i.e. the nexus branch adding it has not deployed
          // yet.
          callTheStoreEnabled:
            merchant.order_feedback_call_the_store_enabled ?? true,
          acknowledgmentMessage:
            merchant.order_feedback_acknowledgment_message ??
            'Thanks for your feedback.',
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, dispatch])
}
